import React, { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { formatName } from "../../../helpers/helpers";
import { canCreateChatChannel, filterChatUsers } from "./chatSearchUtils";
import { getRoleLabel } from "./chatSidebarSectionUtils";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 560,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "85vh",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: 24,
  outline: "none",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

// Same input treatment as the details sheet (rounded, violet focus ring)
const FIELD_SX = {
  backgroundColor: "white",
  borderRadius: "10px",
  fontSize: "14px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  // focused wins over hover (declared last, and explicit for the hover case)
  "&.Mui-focused .MuiOutlinedInput-notchedOutline, &.Mui-focused:hover .MuiOutlinedInput-notchedOutline":
    { borderColor: "#6d28d9", borderWidth: "1px" },
};

const Initial = ({ name, size = 28 }) => (
  <div
    className="flex shrink-0 items-center justify-center rounded-full bg-violet-700 text-white"
    style={{ width: size, height: size, fontSize: Math.round(size * 0.45) }}
  >
    {name?.trim()?.[0]?.toUpperCase() || "?"}
  </div>
);

/**
 * Create a named channel. Members are picked the same way as the New message
 * pane (chips + role pills) and can arrive pre-selected from it.
 */
function CreateChannelModal(props) {
  const [inputValue, setInputValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const canCreateChannel = canCreateChatChannel(
    props.channelName,
    props.selectedMembers
  );

  useEffect(() => {
    if (!props.open) {
      setInputValue("");
      setSubmitting(false);
    }
  }, [props.open]);

  const submit = async () => {
    if (!canCreateChannel || submitting) return;
    setSubmitting(true);
    try {
      await props.handleCreateChannel(
        props.channelName,
        props.selectedMembers,
        props.channelDescription
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={props.open} onClose={() => props.handleClose()}>
      <Box sx={modalStyle}>
        <div className="absolute right-3 top-3 z-10">
          <IconButton size="small" onClick={() => props.handleClose()} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex items-center gap-x-4 px-6 pb-4 pt-6">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-orange-600 text-2xl text-white">
              #
            </div>
            <div className="min-w-0 pr-8">
              <h2 className="text-xl font-semibold text-slate-900">New channel</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                A named place for an ongoing topic. Members can be added or removed later.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-y-4 px-6 pb-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">
                Channel name
              </label>
              <TextField
                size="small"
                fullWidth
                autoFocus
                placeholder="e.g. Openers, Bar team, Catering events"
                value={props.channelName}
                onChange={(event) => props.setChannelName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    submit();
                  }
                }}
                InputProps={{ sx: FIELD_SX }}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">
                Description (optional)
              </label>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                placeholder="What is this channel about?"
                value={props.channelDescription}
                onChange={(event) => props.setChannelDescription(event.target.value)}
                InputProps={{ sx: FIELD_SX }}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">
                Members
              </label>
              <Autocomplete
                multiple
                fullWidth
                autoHighlight
                openOnFocus
                size="small"
                options={(props.chatUsers || []).filter(
                  (user) => user.can_join_store_channel !== false
                )}
                value={props.selectedMembers || []}
                onChange={(event, value) => props.setSelectedMembers(value)}
                inputValue={inputValue}
                onInputChange={(event, value) => setInputValue(value)}
                getOptionLabel={(option) => formatName(option.name)}
                isOptionEqualToValue={(option, value) =>
                  option.id?.toString() === value.id?.toString()
                }
                filterOptions={(options, state) => filterChatUsers(options, state.inputValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      size="small"
                      label={formatName(option.name)}
                      sx={{ bgcolor: "#ede9fe", color: "#5b21b6" }}
                    />
                  ))
                }
                renderOption={(optionProps, option) => {
                  const role = getRoleLabel(option.permission_type);
                  return (
                    <li {...optionProps} key={option.id}>
                      <div className="flex w-full min-w-0 items-center gap-x-3">
                        <Initial name={option.name} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-x-2">
                            <span className="truncate">{formatName(option.name)}</span>
                            {role && (
                              <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-xxs text-slate-600">
                                {role}
                              </span>
                            )}
                          </div>
                          {option.email && (
                            <div className="truncate text-xs text-slate-500">{option.email}</div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      props.selectedMembers?.length ? "" : "Type a name or email"
                    }
                    sx={{ "& .MuiOutlinedInput-root": FIELD_SX }}
                  />
                )}
              />
              <p className="mt-1.5 text-xs text-slate-500">
                Managers and admins you add become moderators automatically.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-x-2 border-t border-slate-100 px-6 py-4">
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            onClick={() => props.handleClose()}
          >
            Cancel
          </button>
          <button
            type="button"
            className="flex min-w-[132px] items-center justify-center rounded-lg bg-violet-700 px-4 py-2 text-sm font-medium text-white hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!canCreateChannel || submitting}
            onClick={submit}
          >
            {submitting ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : (
              "Create channel"
            )}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

export default CreateChannelModal;
